import { useMemo, useState } from 'react';
import { Check, Eye, Layers3, MoreHorizontal, Search, UserPlus, Users } from 'lucide-react';
import { logger } from '../../../shared/utils/logger.js';
import AssignmentManagerModal from '../components/AssignmentManagerModal';
import EmployeeDocumentsModal from '../components/EmployeeDocumentsModal';
import EmployeeEditorModal from '../components/EmployeeEditorModal';
import {
  useCreateEmployee,
  useDeleteEmployee,
  useEmployeeRoles,
  useEmployees,
  useUpdateEmployee,
  useUpdateEmployeeStatus,
} from '../queries';
import type { EmployeeRow, EmployeeWriteInput } from '../api/employees.api';
import type { ApiError } from '../../../shared/api/apiError';

type StatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED';

type EmployeesTabProps = {
  active: boolean;
  onToast?: (toast: { type: 'success' | 'error'; message: string }) => void;
};

const MODULE = 'employees-roles-employee-tab';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const apiMessage = (error: unknown): string => {
  const apiError = error as ApiError | undefined;
  const data = apiError?.data as { message?: unknown; error?: { message?: unknown } } | undefined;
  const direct = typeof data?.message === 'string' ? data.message.trim() : '';
  if (direct) return direct;
  const nested = typeof data?.error?.message === 'string' ? data.error.message.trim() : '';
  return nested;
};

const errorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  const fromApi = apiMessage(error);
  if (fromApi) return fromApi;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const STATUS_PILL_CLASS: Record<string, string> = {
  ACTIVE: 'roles-tab__status roles-tab__status--active',
  INACTIVE: 'roles-tab__status roles-tab__status--inactive',
  ON_LEAVE: 'roles-tab__status roles-tab__status--inactive',
  TERMINATED: 'roles-tab__status roles-tab__status--inactive',
};

const appDisplayLabel = (showInApp: boolean): string => (showInApp ? 'Shown in app' : 'Hidden in app');
const appDisplayShortLabel = (showInApp: boolean): string => (showInApp ? 'Shown' : 'Hidden');
const genderLabel = (gender: string): string => {
  const normalized = String(gender || '').trim().toUpperCase();
  if (normalized === 'MALE') return 'Male';
  if (normalized === 'FEMALE') return 'Female';
  return 'Prefer not to say';
};

const NEXT_STATUS_FOR: Record<string, 'ACTIVE' | 'INACTIVE'> = {
  ACTIVE: 'INACTIVE',
  INACTIVE: 'ACTIVE',
  ON_LEAVE: 'ACTIVE',
  TERMINATED: 'ACTIVE',
};

const initialsFor = (name: string): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
};

const EmployeesTab = ({ active, onToast }: EmployeesTabProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [actionsMenuFor, setActionsMenuFor] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [documentsFor, setDocumentsFor] = useState<EmployeeRow | null>(null);
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [assignmentsFor, setAssignmentsFor] = useState<EmployeeRow | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<EmployeeRow | null>(null);
  const [statusCandidate, setStatusCandidate] = useState<{ row: EmployeeRow; next: 'ACTIVE' | 'INACTIVE' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const employeeFilters = useMemo(
    () => ({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      roleUuid: roleFilter === 'ALL' ? undefined : roleFilter,
    }),
    [statusFilter, roleFilter],
  );

  const employeesQuery = useEmployees(active, employeeFilters);
  const rolesQuery = useEmployeeRoles(active);

  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const statusMutation = useUpdateEmployeeStatus();
  const deleteMutation = useDeleteEmployee();

  const submitting = createMutation.isPending || updateMutation.isPending;
  const submitError = useMemo(
    () => errorMessage(createMutation.error || updateMutation.error, ''),
    [createMutation.error, updateMutation.error],
  );

  const handleOpenCreate = () => {
    logEvent('employees_tab_open_create');
    setActionsMenuFor(null);
    setEditing(null);
    setEditorOpen(true);
  };

  const handleOpenEdit = (row: EmployeeRow) => {
    logEvent('employees_tab_open_edit', { employeeUuid: row.employeeUuid });
    setActionsMenuFor(null);
    setEditing(row);
    setEditorOpen(true);
  };

  const handleClose = () => {
    setEditorOpen(false);
    setEditing(null);
    setActionsMenuFor(null);
    createMutation.reset();
    updateMutation.reset();
  };

  const handleCloseDocuments = () => {
    setDocumentsFor(null);
    setActionsMenuFor(null);
  };

  const handleSubmit = (input: EmployeeWriteInput) => {
    const onDone = (verb: string) => () => {
      onToast?.({ type: 'success', message: `Employee ${verb}.` });
      handleClose();
    };
    const onFail = (verb: string) => (err: unknown) => {
      onToast?.({ type: 'error', message: errorMessage(err, `Could not ${verb} employee.`) });
    };
    if (editing?.employeeUuid) {
      updateMutation.mutate(
        { employeeUuid: editing.employeeUuid, input },
        { onSuccess: onDone('updated'), onError: onFail('update') },
      );
    } else {
      createMutation.mutate(input, {
        onSuccess: onDone('created'),
        onError: onFail('create'),
      });
    }
  };

  const handleToggleStatus = (row: EmployeeRow) => {
    setActionsMenuFor(null);
    const next = NEXT_STATUS_FOR[row.status] || 'ACTIVE';
    logEvent('employees_tab_toggle_status', { employeeUuid: row.employeeUuid, next });
    if (next !== 'ACTIVE') {
      setStatusCandidate({ row, next });
      return;
    }
    statusMutation.mutate(
      { employeeUuid: row.employeeUuid, status: next },
      {
        onSuccess: (saved) =>
          onToast?.({
            type: 'success',
            message: `Employee ${saved.status === 'ACTIVE' ? 'activated' : 'deactivated'}.`,
          }),
        onError: (err) =>
          onToast?.({ type: 'error', message: errorMessage(err, 'Could not update employee status.') }),
      },
    );
  };

  const closeStatusModal = () => {
    logEvent('employees_tab_status_change_cancelled', { employeeUuid: statusCandidate?.row.employeeUuid || null });
    setStatusCandidate(null);
  };

  const confirmStatusChange = () => {
    if (!statusCandidate?.row.employeeUuid) {
      return;
    }
    const { row, next } = statusCandidate;
    logEvent('employees_tab_status_change_confirmed', { employeeUuid: row.employeeUuid, next });
    statusMutation.mutate(
      { employeeUuid: row.employeeUuid, status: next },
      {
        onSuccess: (saved) => {
          onToast?.({
            type: 'success',
            message: `Employee ${saved.status === 'ACTIVE' ? 'activated' : 'deactivated'}.`,
          });
          setStatusCandidate(null);
        },
        onError: (err) =>
          onToast?.({ type: 'error', message: errorMessage(err, 'Could not update employee status.') }),
      },
    );
  };

  const handleDelete = (row: EmployeeRow) => {
    logEvent('employees_tab_delete_requested', { employeeUuid: row.employeeUuid });
    setActionsMenuFor(null);
    setDeleteCandidate(row);
  };

  const closeDeleteModal = () => {
    logEvent('employees_tab_delete_cancelled', { employeeUuid: deleteCandidate?.employeeUuid || null });
    setDeleteCandidate(null);
  };

  const confirmDelete = () => {
    if (!deleteCandidate?.employeeUuid) {
      return;
    }
    logEvent('employees_tab_delete_confirmed', { employeeUuid: deleteCandidate.employeeUuid });
    deleteMutation.mutate(deleteCandidate.employeeUuid, {
      onSuccess: () => {
        onToast?.({ type: 'success', message: 'Employee removed.' });
        setDeleteCandidate(null);
      },
      onError: (err) =>
        onToast?.({ type: 'error', message: errorMessage(err, 'Could not remove employee.') }),
    });
  };

  if (!active) {
    return null;
  }

  const rows = employeesQuery.data || [];
  const allRoles = rolesQuery.data || [];
  const hasAssignableRoles = allRoles.some((role) => role.status === 'ACTIVE');
  const activeEmployees = rows.filter((row) => row.status === 'ACTIVE').length;
  const visibleInApp = rows.filter((row) => row.showInApp).length;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visibleRows = normalizedSearch
    ? rows.filter((row) => [
        row.name,
        row.phone,
        row.email,
        row.role?.name,
        row.status,
        genderLabel(row.gender),
        appDisplayLabel(row.showInApp),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch)))
    : rows;

  return (
    <div className="roles-tab employees-tab">
      <div className="employees-tab__topbar">
        <button
          type="button"
          className="roles-tab__primary employees-tab__primary"
          onClick={handleOpenCreate}
          disabled={employeesQuery.isLoading || rolesQuery.isLoading || !hasAssignableRoles}
          title={
            allRoles.length === 0
              ? 'Create at least one role first'
              : !hasAssignableRoles
                ? 'Activate at least one role before adding an employee'
                : undefined
          }
        >
          <UserPlus size={15} aria-hidden="true" />
          Add employee
        </button>
      </div>

      <div className="employees-tab__stats" aria-label="Employee summary">
        {[
          {
            label: 'Total staff',
            value: rows.length,
            icon: <Users size={18} aria-hidden="true" />,
            tone: 'green',
          },
          {
            label: 'Active now',
            value: activeEmployees,
            icon: <Check size={18} aria-hidden="true" />,
            tone: 'green',
          },
          {
            label: 'Roles',
            value: allRoles.length,
            icon: <Layers3 size={18} aria-hidden="true" />,
            tone: 'cyan',
          },
          {
            label: 'Visible in app',
            value: visibleInApp,
            icon: <Eye size={18} aria-hidden="true" />,
            tone: 'amber',
          },
        ].map((item) => (
          <article key={item.label} className="employees-tab__stat-card">
            <div className={`employees-tab__stat-icon employees-tab__stat-icon--${item.tone}`}>{item.icon}</div>
            <div>
              <div className="employees-tab__stat-value">{item.value}</div>
              <div className="employees-tab__stat-label">{item.label}</div>
            </div>
          </article>
        ))}
      </div>

      <div className="roles-tab__toolbar employees-tab__toolbar">
        <div className="roles-tab__toolbar-group employees-tab__filters">
          <label className="role-editor__field roles-tab__toolbar-field">
            <span className="role-editor__label roles-tab__toolbar-label">Status</span>
            <select
              className="roles-tab__filter"
              value={statusFilter}
              onChange={(e) => {
                const next = e.target.value as StatusFilter;
                logEvent('employees_tab_filter_status_changed', { next });
                setStatusFilter(next);
              }}
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ON_LEAVE">On Leave</option>
              <option value="TERMINATED">Terminated</option>
            </select>
          </label>
          <label className="role-editor__field roles-tab__toolbar-field">
            <span className="role-editor__label roles-tab__toolbar-label">Role</span>
            <select
              className="roles-tab__filter"
              value={roleFilter}
              onChange={(e) => {
                logEvent('employees_tab_filter_role_changed', { roleUuid: e.target.value });
                setRoleFilter(e.target.value);
              }}
            >
              <option value="ALL">All roles</option>
              {allRoles.map((role) => (
                <option key={role.roleUuid} value={role.roleUuid}>{role.name}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="employees-tab__search" aria-label="Search employees">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search name, phone, email..."
          />
        </label>
      </div>

      {employeesQuery.isLoading && <div className="roles-tab__loading">Loading employees…</div>}

      {!employeesQuery.isLoading && employeesQuery.isError && (
        <div className="roles-tab__error" role="alert">
          {errorMessage(employeesQuery.error, 'Could not load employees.')}
        </div>
      )}

      {!employeesQuery.isLoading && !employeesQuery.isError && rows.length === 0 && (
        <div className="roles-tab__empty">
          {allRoles.length === 0
            ? 'Create a role first on the Roles tab, then add your first employee.'
            : !hasAssignableRoles
              ? 'Activate at least one role on the Roles tab before adding an employee.'
              : 'No employees match these filters. Click Add employee to create one.'}
        </div>
      )}

      {!employeesQuery.isLoading && !employeesQuery.isError && rows.length > 0 && visibleRows.length === 0 && (
        <div className="roles-tab__empty">
          No employees match this search. Try a name, phone number, email, or role.
        </div>
      )}

      {!employeesQuery.isLoading && !employeesQuery.isError && visibleRows.length > 0 && (
        <table className="roles-tab__table employees-tab__table" aria-label="Employees">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Gender</th>
              <th>App display</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              const pillClass = STATUS_PILL_CLASS[row.status] || STATUS_PILL_CLASS.INACTIVE;
              return (
                <tr key={row.employeeUuid}>
                  <td data-label="Name">
                    <div className="employees-tab__name-cell">
                      <span className="employees-tab__avatar" aria-hidden="true">{initialsFor(row.name)}</span>
                      <strong>{row.name}</strong>
                    </div>
                  </td>
                  <td data-label="Role">
                    {row.role?.name ? (
                      <span className="employees-tab__role-chip">{row.role.name}</span>
                    ) : (
                      <span className="employees-tab__muted">—</span>
                    )}
                  </td>
                  <td data-label="Contact" className="roles-tab__contact-cell">
                    {row.phone || row.email ? (
                      <>
                        {row.phone && <div>{row.phone}</div>}
                        {row.email && <div className="roles-tab__contact-secondary">{row.email}</div>}
                      </>
                    ) : (
                      <span className="employees-tab__muted">—</span>
                    )}
                  </td>
                  <td data-label="Status">
                    <span className={pillClass}>{row.status}</span>
                  </td>
                  <td data-label="Gender">{genderLabel(row.gender)}</td>
                  <td data-label="App display">
                    <span className="employees-tab__app-display">
                      <Eye size={14} aria-hidden="true" />
                      {appDisplayShortLabel(row.showInApp)}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <div className="roles-tab__row-actions">
                      <button
                        type="button"
                        className="roles-tab__row-action"
                        onClick={() => {
                          setActionsMenuFor(null);
                          logEvent('employees_tab_open_documents', { employeeUuid: row.employeeUuid });
                          setDocumentsFor(row);
                        }}
                        disabled={submitting || statusMutation.isPending}
                      >
                        Documents
                      </button>
                      <button
                        type="button"
                        className="roles-tab__row-action"
                        onClick={() => {
                          setActionsMenuFor(null);
                          logEvent('employees_tab_open_assignments', { employeeUuid: row.employeeUuid });
                          setAssignmentsFor(row);
                        }}
                      >
                        Services
                      </button>
                      <div className="roles-tab__menu">
                        <button
                          type="button"
                          className="roles-tab__row-action roles-tab__row-action--icon"
                          aria-label={`More actions for ${row.name}`}
                          aria-haspopup="menu"
                          aria-expanded={actionsMenuFor === row.employeeUuid}
                          onClick={() =>
                            setActionsMenuFor((current) =>
                              current === row.employeeUuid ? null : row.employeeUuid,
                            )
                          }
                          disabled={submitting || statusMutation.isPending || deleteMutation.isPending}
                        >
                          <MoreHorizontal size={16} aria-hidden="true" />
                        </button>
                        {actionsMenuFor === row.employeeUuid ? (
                          <div className="roles-tab__menu-popover" role="menu" aria-label={`Actions for ${row.name}`}>
                            <button
                              type="button"
                              className="roles-tab__menu-item"
                              role="menuitem"
                              onClick={() => handleOpenEdit(row)}
                              disabled={submitting}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="roles-tab__menu-item"
                              role="menuitem"
                              onClick={() => handleToggleStatus(row)}
                              disabled={statusMutation.isPending}
                            >
                              {row.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              type="button"
                              className="roles-tab__menu-item roles-tab__menu-item--danger"
                              role="menuitem"
                              onClick={() => handleDelete(row)}
                              disabled={deleteMutation.isPending}
                            >
                              Remove
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <EmployeeEditorModal
        open={editorOpen}
        initial={editing}
        rolesAvailable={allRoles}
        rolesLoading={rolesQuery.isLoading}
        submitting={submitting}
        errorMessage={submitError}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      <EmployeeDocumentsModal
        open={Boolean(documentsFor)}
        employee={documentsFor}
        onClose={handleCloseDocuments}
        onToast={onToast}
      />

      {assignmentsFor && (
        <AssignmentManagerModal
          open
          mode="by-employee"
          employeeUuid={assignmentsFor.employeeUuid}
          employeeName={assignmentsFor.name}
          onClose={() => {
            logEvent('employees_tab_close_assignments', {
              employeeUuid: assignmentsFor.employeeUuid,
            });
            setAssignmentsFor(null);
          }}
          onToast={onToast}
        />
      )}

      {deleteCandidate ? (
        <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Remove employee">
          <div className="role-editor role-editor--confirm">
            <div className="role-editor__confirm-header">
              <h3 className="role-editor__title">Remove employee</h3>
              <p className="role-editor__confirm-copy">
                This will remove <strong>{deleteCandidate.name}</strong> from this store’s employees workspace.
                This action cannot be undone.
              </p>
            </div>

            <div className="role-editor__impact-block">
              <div className="role-editor__impact-title">Employee details</div>
              <div className="role-editor__impact-item" role="listitem">
                <div>
                  <div className="role-editor__impact-name">{deleteCandidate.name}</div>
                  <div className="role-editor__impact-meta">
                    {deleteCandidate.role?.name || 'No role'} • {deleteCandidate.email || deleteCandidate.phone || deleteCandidate.status}
                  </div>
                </div>
                <span className={STATUS_PILL_CLASS[deleteCandidate.status] || STATUS_PILL_CLASS.INACTIVE}>
                  {deleteCandidate.status}
                </span>
              </div>
              <p className="role-editor__impact-warning" role="alert">
                Removing this employee deletes the staffing record from this store.
                If they are assigned to any services, those service assignments will be removed automatically.
              </p>
            </div>

            <div className="role-editor__actions">
              <button
                type="button"
                className="role-editor__cancel"
                onClick={closeDeleteModal}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="role-editor__submit role-editor__submit--danger"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Removing…' : 'Remove employee'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {statusCandidate ? (
        <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Deactivate employee">
          <div className="role-editor role-editor--confirm">
            <div className="role-editor__confirm-header">
              <h3 className="role-editor__title">Deactivate employee</h3>
              <p className="role-editor__confirm-copy">
                This will deactivate <strong>{statusCandidate.row.name}</strong> in this store’s employees workspace.
              </p>
            </div>

            <div className="role-editor__impact-block">
              <div className="role-editor__impact-title">Impact</div>
              <div className="role-editor__impact-item" role="listitem">
                <div>
                  <div className="role-editor__impact-name">{statusCandidate.row.name}</div>
                  <div className="role-editor__impact-meta">
                    {statusCandidate.row.role?.name || 'No role'} • {statusCandidate.row.email || statusCandidate.row.phone || statusCandidate.row.status}
                  </div>
                </div>
                <span className={STATUS_PILL_CLASS[statusCandidate.row.status] || STATUS_PILL_CLASS.INACTIVE}>
                  {statusCandidate.row.status}
                </span>
              </div>
              <p className="role-editor__impact-warning" role="alert">
                If this employee is assigned to any services, those service assignments will be removed automatically when you deactivate them.
              </p>
            </div>

            <div className="role-editor__actions">
              <button
                type="button"
                className="role-editor__cancel"
                onClick={closeStatusModal}
                disabled={statusMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="role-editor__submit role-editor__submit--danger"
                onClick={confirmStatusChange}
                disabled={statusMutation.isPending}
              >
                {statusMutation.isPending ? 'Deactivating…' : 'Deactivate employee'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EmployeesTab;
